import { Component } from 'react';
import { Card, Button } from 'react-bootstrap';
import { GoX } from 'react-icons/go';
import Tooltip from "../../../controls/common/tooltip";
import { Converter } from "../../workspace/converter";
import SessionContext from "../../session/context";
import WidgetStatus from "./widgetStatus";
import "./index.css";

export default class WidgetBase extends Component {
  static contextType = SessionContext;

  constructor(props) {
    super(props);
    this.subscribedAliases = new Set();
    this.discoveredAliases = new Set();
    this.cleanupFns = {};
  }

  loadObjectValue(component, route, type) {
    const alias = Converter.getComponentPortValueData(component, route);
    if ( !alias ) {
      return [null, { variant: "info", message: `Connect a value to the "${route}" port.` }];
    }

    // Use context which is SessionManager; also support explicit static usage (backward compat).
    const sessionManager = this.context;
    const manager = sessionManager || arguments[0];

    if ( !manager || !manager.session ) {
      return [null, { variant: "info", message: "Select a session to view this widget." }];
    }

    const value = manager.getSessionData(alias);

    // Register alias for subscription
    this.discoveredAliases.add(alias);

    if ( !value ) {
      return [null, { variant: "info", message: `"${alias}" is not available in the selected session yet.` }];
    }

    const valueType = value["type"];
    if ( type !== valueType ) {
      return [null, {
        variant: "warning",
        message: `The "${route}" port expects a "${type}" value, but "${alias}" is "${valueType}".`
      }];
    }

    return [value, null];
  }

  componentDidMount() {
    this.updateSubscriptions();
    this.sessionListener = () => this.forceUpdate();
    if (this.context) {
      this.context.addListener(this.sessionListener);
    }
  }

  componentDidUpdate() {
    this.updateSubscriptions();
  }

  componentWillUnmount() {
    // Unsubscribe all
    for (const alias in this.cleanupFns) {
      this.cleanupFns[alias]();
    }
    this.cleanupFns = {};
    this.subscribedAliases.clear();

    if (this.context && this.sessionListener) {
      this.context.removeListener(this.sessionListener);
    }
    this.sessionListener = null;
  }

  updateSubscriptions() {
    const manager = this.context;
    if (!manager) return;

    // Unsubscribe from stale
    for (const alias of this.subscribedAliases) {
      if (!this.discoveredAliases.has(alias)) {
        if (this.cleanupFns[alias]) {
          this.cleanupFns[alias]();
          delete this.cleanupFns[alias];
        }
        this.subscribedAliases.delete(alias);
      }
    }

    // Subscribe to new
    for (const alias of this.discoveredAliases) {
      if (!this.subscribedAliases.has(alias)) {
        this.cleanupFns[alias] = manager.subscribe(alias, () => {
          this.forceUpdate();
        });
        this.subscribedAliases.add(alias);
      }
    }

    // Reset discovered for next render
    this.discoveredAliases.clear();
  }

  renderNotice(notice) {
    if ( !notice ) return null;
    // Pass the variant straight through. General widget alerts use info/warning
    // (standard Alert styling, consistent with the rest of the app);
    // content-internal callers pass "light" for the subtle box.
    return <WidgetStatus variant={notice.variant || "info"}>{notice.message}</WidgetStatus>;
  }

  renderLoading(message) {
    // Loading is a general widget state -> primary Alert (matches the app-wide
    // "Loading …" convention, e.g. "Loading revision"), with an inline spinner.
    return <WidgetStatus variant="primary" loading>{message}</WidgetStatus>;
  }

  renderBody() {
    return (
      <Card.Body>
        Widget content implementation is missing.
      </Card.Body>
    );
  }

  getClassName() {
    return "";
  }

  getEffectiveSize() {
    if (this.props.data?.layoutType === "grid") {
      return { width: "100%", height: "100%", maxHeight: "100%" };
    }
    const w = Converter.getComponentPortValueData(this.props.data.component, "width");
    const h = Converter.getComponentPortValueData(this.props.data.component, "height");
    return { width: `${w}px`, height: `${h}px`, maxHeight: `${h}px` };
  }

  renderContent() {
    const { width, height, maxHeight } = this.getEffectiveSize();

    return (
      <Card.Body className="nodrag nowheel" style={{
        width,
        height,
        maxHeight,
        overflow: "scroll"
      }}>
        {this.renderBody()}
      </Card.Body>
    );
  }

  isSelected() {
    const shellInterface = this.props.data?.shellInterface;
    if (!shellInterface || !shellInterface.getState) return false;
    const state = shellInterface.getState();
    const componentId = this.props.data?.component?.id;
    return state?.details?.view === "component" && state?.details?.data?.id === componentId;
  }

  removeComponent = (event) => {
    event.stopPropagation();
    const shellInterface = this.props.data?.shellInterface;
    const componentId = this.props.data?.component?.id;
    if (!shellInterface || !shellInterface.getState || !componentId) return;
    const state = shellInterface.getState();
    const nextNodes = (state.nodes || []).filter((n) => n.id !== componentId);
    const workflowInterface = state.workflowInterface;
    if (workflowInterface && workflowInterface.setNodes) {
      workflowInterface.setNodes(nextNodes, /*isModified=*/true);
    }
    // Drop the connections that referenced the removed component (free-form only;
    // grid playgrounds don't carry connections, so the filter is a no-op).
    const nextEdges = (state.edges || []).filter(
      (e) => e.source !== componentId && e.target !== componentId
    );
    if (workflowInterface && workflowInterface.setEdges && nextEdges.length !== (state.edges || []).length) {
      workflowInterface.setEdges(nextEdges, /*isModified=*/true);
    }
    // Clear the details pane.
    if (shellInterface.updateDetails) {
      shellInterface.updateDetails(null, null);
    }
  };

  renderHeaderControls() {
    const canEdit = !!this.props.data?.shellInterface?.canEditRevision?.();
    return (
      <span className="playground-widget-controls float-right" onClick={(e) => e.stopPropagation()}>
        <span className="text-muted playground-widget-type">
          {this.props.data.component.type}
        </span>
        {canEdit && (
          <Tooltip text="Remove widget">
            <Button
              variant="light"
              size="sm"
              onClick={this.removeComponent}
            >
              <GoX />
            </Button>
          </Tooltip>
        )}
      </span>
    );
  }

  // Override points for envelope-backed widgets (Thread, Message). Default
  // to null so generic widgets keep their current header unchanged.
  renderHeaderAlias() {
    return null;
  }

  renderHeaderActions() {
    return null;
  }

  render() {
    // Clear discovered aliases at the beginning of render so subscription
    // accumulation starts fresh on each pass.
    this.discoveredAliases.clear();

    const isGrid = this.props.data?.layoutType === "grid";
    const isSelected = this.isSelected();
    const cardClassName = [
      "playground-widget",
      this.getClassName() || "",
      isGrid ? "playground-widget-grid" : "",
      isSelected ? "playground-widget-selected" : "",
    ].filter(Boolean).join(" ");

    // The header is a permanent in-flow top bar on every widget — always
    // rendered (not just on selection); see common/index.css.
    const renderHeader = true;

    // Per-widget content zoom (grid mode only). Comes from the playground
    // shell's zoom slider — the layout itself stays at 1x so widget cells
    // remain aligned to the grid; only what's inside each cell scales.
    const widgetZoom = this.props.data?.zoom ?? 1;
    const contentStyle = isGrid ? { zoom: widgetZoom } : undefined;

    return (
      <Card className={cardClassName}>
        {renderHeader && (
          <Card.Header>
            <span className="playground-widget-header-left">
              <span className="playground-widget-name">
                {this.props.data.component.name}
              </span>
              {this.renderHeaderAlias()}
            </span>
            <span className="playground-widget-header-right">
              {this.renderHeaderActions()}
              {this.renderHeaderControls()}
            </span>
          </Card.Header>
        )}
        <div className="playground-widget-content" style={contentStyle}>
          {this.renderContent()}
        </div>
      </Card>
    );
  }
}
